import { Component, OnInit, Inject, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { IRatingPeriod } from '@core/interfaces/iratingperiod';
import { ISites } from '@core/interfaces/isites';
import { AgencyService } from '@core/services/agency.service';
import { AuthService } from '@core/services/auth.service';
import { ClassService } from '@core/services/class.service';
import { ClassroomService } from '@core/services/classroom.service';
import { LookupService } from '@core/services/lookup.service';
import { RatingPeriodService } from '@core/services/rating-period.service';
import { SiteService } from '@core/services/site.service';
import { StudentService } from '@core/services/student.service';
import { StudentClassEnrollmentService } from '@core/services/student-class-enrollment.service';
import { ToastService } from '@core/services/toast.service';
import { SelectType } from '@core/enums/select';
import { Observable, Subscription, take } from 'rxjs';
import { IClass } from '@core/interfaces/iclasses';
import { FilterSelectComponent } from '@shared/components/dropdowns/filter-select/filter-select.component';
import { StateService } from '@core/services/state.service';
import { RatingViewVersionEnum } from '@core/enums/rating-view-version.enum';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-assign-child',
    templateUrl: './assign-child.component.html',
    styleUrls: [
        './assign-child.component.scss',
        './../add-new-child.component.scss',
        './../../../../shared/components/drdp-tables/add-edit/add-edit.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AssignChildComponent implements OnInit {
  @Input() enrollDroppedChild: boolean = false;
  @ViewChild('classroomSelect') classroomSelect:
    | FilterSelectComponent
    | undefined;
  get siteId() {
    return this.assignStudentForm.get('siteId');
  }
  get agencyId() {
    return this.assignStudentForm.get('agencyId');
  }
  get ratingPeriodId() {
    return this.assignStudentForm.get('ratingPeriodId');
  }
  get classId() {
    return this.assignStudentForm.get('classId');
  }
  get classEnrollmentDate() {
    return this.assignStudentForm.get('classEnrollmentDate');
  }
  get ageGroupInstrument2025Id() {
    return this.assignStudentForm.get('ageGroupInstrument2025Id');
  }
  public get selectType() {
    return SelectType;
  }

  get requireAgeGrade() {
    return this.enforceStateConfig ? this.isRatingView2025 : this.isSoftImplementation;
  }

  classroomPlaceholder: string = 'Set site for classroom options';
  rpPlaceholder: string = 'Set agency for rating periods';
  sitePlaceholder: string = 'Set agency for sites';
  currentYear: number = 0;
  selectedSchoolYearId: number | null = null;
  assignStudentForm: FormGroup | any;
  sites: ISites[] = [];
  ratingPeriods: IRatingPeriod[] = [];
  classrooms: IClass[] = [];
  optionsLoaded: boolean = false;
  selectedRpMinDate!: Date;
  selectedRpMaxDate!: Date;
  isSubmitting: boolean = false;
  enforceStateConfig: boolean = false;
  isRatingView2025: boolean = false;
  ageGradeIconInfo: string = '';
  private subscriptions = new Subscription();

  user: ILoginResponse = this.auth.getCurrentUser();
  stateSearchId? = this.user.stateId;
  userAgencyId? = this.user.agencyId;

  assignmentDropdowns$!: Observable<any>;
  initialAgencyId!: number;
  canViewMultipleAgencies!: boolean;
  selectClass = SelectType.Class;

  addStudentPayload: any | undefined = null;

  isDropdownRequired = {
    ratingPeriod: true,
    site: true,
    classroom: true,
  };

  agencySoftImplementationEnabled = false;
  siteSoftImplementationEnabled = false;
  classroomSoftImplementationEnabled = false;
  isSoftImplementation = false;

  constructor(
    public fb: FormBuilder,
    public lookup: LookupService,
    public agency: AgencyService,
    public siteService: SiteService,
    public auth: AuthService,
    public ratingPeriod: RatingPeriodService,
    public classroomService: ClassroomService,
    public classService: ClassService,
    public student: StudentService,
    public studentClassEnrollment: StudentClassEnrollmentService,
    public toastService: ToastService,
    public lookupService: LookupService,
    private stateService: StateService,
    private translocoService: TranslocoService,
    public dialogRef: MatDialogRef<AssignChildComponent>,
    @Inject(MAT_DIALOG_DATA) public addChildFormData: any
  ) {
  }

  ngOnInit(): void {
    this.addStudentPayload = this.addChildFormData;
    this.initializeForm();
    this.canViewMultipleAgencies =
      this.auth.isSuperAdmin() || this.auth.isCustomerService();
  }

  initializeForm(): void {
    this.assignStudentForm = this.fb.group({
      agencyId: [this.userAgencyId, [Validators.required]],
      siteId: [null, Validators.required],
      ratingPeriodId: [null, Validators.required],
      classId: [null, Validators.required],
      classEnrollmentDate: [null, [Validators.required]],
      ageGroupInstrument2025Id: [null]
    });

    this.agency.getAgency(this.userAgencyId).pipe(take(1)).subscribe((agency) => {
      this.agencySoftImplementationEnabled = agency.isSoftImplementation ?? false;
      this.updateSoftImplementation();
    });

    this.updateFormValidation();
  }

  updateFormValidation(): void {
    if (this.requireAgeGrade) {
      this.ageGroupInstrument2025Id?.setValidators([Validators.required]);
      if (!this.ageGroupInstrument2025Id?.value) {
        this.ageGroupInstrument2025Id?.setValue(this.addStudentPayload?.ageGradeInstrumentId ?? null);
      }
    } else {
      this.ageGroupInstrument2025Id?.setValue(null);
      this.ageGroupInstrument2025Id?.clearValidators();
    }

    const p3Info = this.translocoService.translate('user.addNewChild.assign.p3Info');
    this.ageGradeIconInfo = this.enforceStateConfig && this.isRatingView2025
      ? `${p3Info} ${this.translocoService.translate('user.addNewChild.assign.schoolAgeInfo')}`
      : p3Info;
    this.ageGroupInstrument2025Id?.updateValueAndValidity();
  }

  handleDropdown(event: any, type: string): void {
    switch (type) {
      case SelectType.States:
        this.stateSearchId = event?.id;
        this.agencyId.setValue(null);
        this.siteId.setValue(null);
        this.ratingPeriodId.setValue(null);
        this.classId.setValue(null);
        break;
      case SelectType.Agencies:
        this.agencyId.setValue(event?.id);
        this.agencySoftImplementationEnabled = event?.isSoftImplementation?
              event?.isSoftImplementation : event?.item?.isSoftImplementation;
        this.ratingPeriodId.setValue(null);
        this.siteId.setValue(null);
        this.classId.setValue(null);
        break;
      case SelectType.Sites:
        this.siteId.setValue(event?.id);
        this.siteSoftImplementationEnabled = event?.isSoftImplementation ?
              event?.isSoftImplementation : event?.item?.isSoftImplementation;
        this.classId.setValue(null);
        break;
      case SelectType.RatingPeriods:
        this.ratingPeriodId.setValue(event?.id);
        this.selectedRpMinDate = new Date(event?.minDate);
        this.selectedRpMaxDate = new Date(event?.maxDate);
        this.validateDate();
        this.enforceStateConfig = event?.schoolYear?.enforceStateConfig ?? false;
        this.selectedSchoolYearId = event?.schoolYearId ?? null;
        this.checkStateConfiguration();
        break;
      case SelectType.Class:
        this.classId.setValue(event?.id);
        this.classroomSoftImplementationEnabled = event?.isSoftImplementation ?
              event?.isSoftImplementation : event?.item?.isSoftImplementation;
        break;
      case SelectType.AgeInstrument:
        this.ageGroupInstrument2025Id.setValue(event?.id);
        break;
    }
    this.updateSoftImplementation();
  }

  updateSoftImplementation(): void {
    this.isSoftImplementation = this.classroomSoftImplementationEnabled ||
                               this.siteSoftImplementationEnabled ||
                               this.agencySoftImplementationEnabled;
    this.updateFormValidation();
  }

  checkStateConfiguration(): void {
    if (this.stateSearchId) {
      this.stateService.getStatesById(this.stateSearchId).pipe(take(1)).subscribe(state => {
        this.isRatingView2025 = state.ratingViewVersionId === RatingViewVersionEnum.VERSION_2025;
        this.updateFormValidation();
      });
    }
  }

  validateDate() {
    const classEnrollmentDateValue = this.classEnrollmentDate.value;
    if (classEnrollmentDateValue) {
      const classroomEnrollmentDate = new Date(classEnrollmentDateValue).getTime();
      const agencyEnrollmentDate = new Date(this.addStudentPayload.enrollmentStartDate).getTime();

      if (classroomEnrollmentDate < agencyEnrollmentDate) {
        this.classEnrollmentDate.setErrors({agencyDate: true });
      } else if (this.ratingPeriodId.value) {
        const enrollmentDate = new Date(classEnrollmentDateValue);
        enrollmentDate.setHours(0, 0, 0, 0);
        const classEnrollmentDate = enrollmentDate.getTime();
        if ( classEnrollmentDate < this.selectedRpMinDate.getTime() || classEnrollmentDate > this.selectedRpMaxDate.getTime()) {
          this.classEnrollmentDate.setErrors({ dateRange: true });
        } else {
          this.classEnrollmentDate.setErrors(null);
        }
      } else {
        this.classEnrollmentDate.setErrors(null);
      }
    }
  }

  onSubmit(): void {
    if (this.isSubmitting) return;
    if (!this.assignStudentForm.valid) {
      this.toastService.error(this.translocoService.translate('user.addNewChild.common.completeFormToast'));
      return;
    }
    this.isSubmitting = true;
    if (this.addStudentPayload.enrollDroppedChild) {
      const payload = {
        drdpId: this.addStudentPayload.drdpId,
        classId: this.classId.value,
        ratingPeriodId: this.ratingPeriodId.value,
        classEnrollmentDate: this.classEnrollmentDate.value,
        agencyId: this.agencyId.value,
        agencyStartDate: this.addStudentPayload.enrollmentStartDate,
        isSoftImplementation: this.isSoftImplementation,
        ageGroupInstrument2025Id: this.ageGroupInstrument2025Id?.value
      };

      this.subscriptions.add(
        this.studentClassEnrollment
          .enrollDroppedChild(payload)
          .subscribe({
            next: (success: boolean) => {
              if (success) this.dialogRef.close(true);
              else this.isSubmitting = false;
            },
            error: () => (this.isSubmitting = false)
          })
      );
    } else {
      let studentEnrollment;

      if (this.requireAgeGrade) {
        studentEnrollment = {
          ...this.assignStudentForm.value,
          isSoftImplementation: this.isSoftImplementation,
          ageGradeInstrumentId: this.ageGroupInstrument2025Id?.value
        };
      } else {
        studentEnrollment = this.assignStudentForm.value;
      }

      this.addStudentPayload.studentClassEnrollments = [studentEnrollment];
      this.subscriptions.add(
        this.student
          .addAndEnrollStudent(this.addStudentPayload)
          .subscribe({
            next: (res: any) => {
              if (res > 0) {
                this.dialogRef.close(res);
              } else {
                this.isSubmitting = false;
              }
            },
            error: () => (this.isSubmitting = false)
          })
      );
    }
  }

  handleDate(event: any): void {
    this.classEnrollmentDate.setValue(event);
    this.validateDate();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
